import { ref } from 'vue';

export interface ShippingCheckResponse {
  success: boolean;
  available: boolean;
  shippingType: 'Ground' | 'Apex' | null;
  message: string;
  isDelhi?: boolean;
}

export const useShippingAvailability = () => {
  const isChecking = ref(false);
  const isAvailable = ref<boolean | null>(null);
  const shippingType = ref<'Ground' | 'Apex' | null>(null);
  const statusMessage = ref<string>('');
  const checkedPincode = ref<string>('');
  const isDelhi = ref<boolean>(false);

  const resetShipping = () => {
    isChecking.value = false;
    isAvailable.value = null;
    shippingType.value = null;
    statusMessage.value = '';
    checkedPincode.value = '';
    isDelhi.value = false;
  };

  const checkShipping = async (pinCode: string | undefined | null) => {
    const cleaned = (pinCode || '').toString().trim();

    if (!cleaned || !/^\d{6}$/.test(cleaned)) {
      resetShipping();
      return;
    }

    // Skip redundant network call if already checked this exact pincode
    if (checkedPincode.value === cleaned && isAvailable.value !== null) {
      return;
    }

    isChecking.value = true;
    checkedPincode.value = cleaned;

    try {
      const data = await $fetch<ShippingCheckResponse>('/api/shipping/check-availability', {
        method: 'POST',
        body: { pinCode: cleaned },
      });

      if (data && typeof data.available === 'boolean') {
        isAvailable.value = data.available;
        shippingType.value = data.shippingType || null;
        statusMessage.value = data.message || '';
        isDelhi.value = !!data.isDelhi;
      } else {
        isAvailable.value = false;
        shippingType.value = null;
        statusMessage.value = 'Shipping is currently unavailable for this pincode.';
      }
    } catch (error) {
      console.error('[useShippingAvailability] Error checking shipping:', error);
      isAvailable.value = false;
      shippingType.value = null;
      statusMessage.value = 'Shipping is currently unavailable for this pincode.';
    } finally {
      isChecking.value = false;
    }
  };

  return {
    isChecking,
    isAvailable,
    shippingType,
    statusMessage,
    checkedPincode,
    isDelhi,
    checkShipping,
    resetShipping,
  };
};
